import { namedStyleCss } from 'Shared/frontend/styles/venueCustomStyles';
import { useStyleDetails } from 'Shared/frontend/widgets/StylesProvider';
import { useSettings } from 'Shared/frontend/SettingsProvider';
import { buttonsCategory, buttonStyleTag, subNavButtonStyleTag, StyleTag }
	from 'Shared/model/venueSettings';


/*
	The venue styling a button wears, as a flat css object to lay over the base BaseSettings builds -
	css(theme().button, buttonCss()) and css(theme().subNav.button, subNavButtonCss()).

	Read at the button rather than folded into the theme, because the answer depends on where the
	button is: a 'Buttons > Button style' tag in the site-wide list dresses every button on the site,
	the same tag on a page's own style list dresses that page's, and one in a Styles widget dresses
	that subtree's. The theme is built once per venue and knows none of that.

	Call these from inside a derived style function - the () => css(...) the button call sites already
	use - so the css follows the style chain when it changes. The event page needs this: its
	<Shortcuts> row renders before <EventPageContents> calls initPageStyles(), so the chain arrives
	after the first render.
*/
export function buttonCss(): object
{
	return chosenStyleCss(buttonStyleTag);
}

export function subNavButtonCss(): object
{
	return chosenStyleCss(subNavButtonStyleTag);
}


function chosenStyleCss(tag:string): object
{
	const name = chosenStyleName(tag);
	if (name == '')
		return {};

	const settings = useSettings();
	return namedStyleCss(settings.customStyles(), name, settings.palette());
}


/*
	The style the innermost tag of this type names.

	The site-wide list rides the chain ahead of the page's own tags and a widget's ride behind them
	(initPageStyles / Styles), so the last match is the innermost one: taking it and stopping is what
	makes an override replace rather than patch. A tag whose dropdown is still empty is passed over,
	so a freshly ticked one falls through to the level outside it rather than stripping the button.

	An empty chain is not a page with no styles turned on - initPageStyles always pushes the four
	wrap-element tags ahead of everything - it is a frontend component drawn outside a page, which is
	the menu designer's item preview (PreviewTab.tsx wraps them in a SettingsProvider and nothing
	else). The site-wide list stands in there, so the preview shows the venue's buttons rather than
	bare ones.
*/
function chosenStyleName(tag:string): string
{
	const details = useStyleDetails()();

	const chain: StyleTag[] = details.length > 0
		? details.map(entry => ({category:entry.category, type:entry.tag, params:entry.params}))
		: useSettings().siteStyles();

	const chosen = chain.findLast(entry =>
		entry.category==buttonsCategory && entry.type==tag && (entry.params?.style ?? '') != '');

	return chosen?.params?.style ?? '';
}
